import type {
  ActiveCustomerWithSponsorsItem,
  WatchListCustomer,
  WatchListItem,
} from "../../../../Store/Reducers/OpsSlice";
import { isRole, ROLES } from "../../../../Utils/Constants/RoleConstants";

type AccountCatalogItem = Pick<
  ActiveCustomerWithSponsorsItem,
  "customer_id" | "name" | "primary_sponsors"
> & {
  assigned_at?: string | null;
  assigned_on?: string | null;
};

/** Stable id for catalog / briefing rows (customer_id preferred, then id). */
export function resolveAccountRecordId(
  account: { customer_id?: unknown; id?: unknown } | null | undefined
): string {
  return String(account?.customer_id || account?.id || "").trim();
}

export function resolveWatchListCustomerId(
  customer: WatchListCustomer,
  accountsCatalog: AccountCatalogItem[] = []
): string {
  const direct = String(customer.customer_id || "").trim();
  if (direct) return direct;

  const nameKey = String(customer.name || "")
    .trim()
    .toLowerCase();
  if (!nameKey) return "";

  const match = accountsCatalog.find(
    account =>
      String(account.name || "")
        .trim()
        .toLowerCase() === nameKey
  );
  return match ? String(match.customer_id || "").trim() : "";
}

export function getWatchListCustomerIds(
  watchList: WatchListItem,
  accountsCatalog: AccountCatalogItem[] = []
): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];

  (watchList.customers || []).forEach(customer => {
    const customerId = resolveWatchListCustomerId(customer, accountsCatalog);
    if (!customerId || seen.has(customerId)) return;
    seen.add(customerId);
    ids.push(customerId);
  });

  return ids;
}

/** Unique customer IDs across every watch list. */
export function getAllMonitoredCustomerIds(
  watchLists: WatchListItem[],
  accountsCatalog: AccountCatalogItem[] = []
): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];

  watchLists.forEach(watchList => {
    getWatchListCustomerIds(watchList, accountsCatalog).forEach(id => {
      if (seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
  });

  return ids;
}

export function getWatchListAccountCount(watchList: WatchListItem): number {
  if (typeof watchList.customer_count === "number") {
    return watchList.customer_count;
  }
  return watchList.customers?.length ?? 0;
}

export function getTotalMonitoredAccountCount(
  watchLists: WatchListItem[],
  accountsCatalog: AccountCatalogItem[] = []
): number {
  return getAllMonitoredCustomerIds(watchLists, accountsCatalog).length;
}

export function canAccessExecutiveMonitoredTab(
  role: string | { name?: string } | null | undefined
): boolean {
  return (
    isRole(role, ROLES.EXECUTIVE_SPONSOR) ||
    isRole(role, ROLES.EXECUTIVE) ||
    isRole(role, ROLES.C_LEVEL)
  );
}

export function mapExecutiveSidebarAccounts(
  executiveAccountsData: Array<Record<string, unknown>>,
  accountsCatalog: AccountCatalogItem[] = [],
  searchQuery = ""
): Array<{
  id: string;
  name: string;
  sponsorLabel: string;
  relationshipType?: string | null;
}> {
  const accountsById = new Map<string, AccountCatalogItem>();
  accountsCatalog.forEach(account => {
    const id = String(account.customer_id || "").trim();
    if (id) accountsById.set(id, account);
  });

  const query = searchQuery.trim().toLowerCase();

  return Array.from(
    new Map(
      executiveAccountsData
        .map(account => {
          const id = String(
            account.account_id || account.customer_id || account.id || ""
          ).trim();
          if (!id) return null;

          const sourceAccount = accountsById.get(id);
          const name =
            String(
              sourceAccount?.name ||
                account.account_name ||
                account.name ||
                "Unknown Account"
            ).trim() || "Unknown Account";

          const sponsorLabel =
            String(account.primary_sponsors || "").trim() || "Unassigned";

          const relationshipType = String(
            account.relationship_type || ""
          ).trim();

          return {
            id,
            name,
            sponsorLabel,
            relationshipType: relationshipType || null,
          };
        })
        .filter(Boolean)
        .map(account => [account!.id, account!])
    ).values()
  )
    .filter(account =>
      query ? account.name.toLowerCase().includes(query) : true
    )
    .sort((left, right) =>
      left.name.localeCompare(right.name, undefined, {
        sensitivity: "base",
      })
    );
}

export function getExecutiveAccountIds(
  executiveAccountsData: Array<Record<string, unknown>>
): string[] {
  const ids: string[] = [];

  executiveAccountsData.forEach(account => {
    const id = String(
      account.account_id || account.customer_id || account.id || ""
    ).trim();
    if (!id || ids.includes(id)) return;
    ids.push(id);
  });

  return ids;
}

/** Monitor API rows for the Others tab (excludes extra-sponsor-only accounts). */
export function getMonitoredOthersAccountIds(
  monitoredAccountsData: Array<Record<string, unknown>>,
  executiveAccountsData: Array<Record<string, unknown>> = []
): string[] {
  const executiveIdSet = new Set(getExecutiveAccountIds(executiveAccountsData));
  const ids: string[] = [];

  monitoredAccountsData.forEach(account => {
    const id = String(account.account_id || account.customer_id || "").trim();
    if (!id || executiveIdSet.has(id) || ids.includes(id)) return;
    ids.push(id);
  });

  return ids;
}

export function mapMonitoredOthersSidebarAccounts(
  monitoredAccountsData: Array<Record<string, unknown>>,
  executiveAccountsData: Array<Record<string, unknown>> = [],
  accountsCatalog: AccountCatalogItem[] = [],
  searchQuery = ""
): Array<{
  id: string;
  name: string;
  monitorId?: string;
  sponsorLabel: string;
}> {
  const executiveIdSet = new Set(getExecutiveAccountIds(executiveAccountsData));
  const accountsById = new Map<string, AccountCatalogItem>();
  accountsCatalog.forEach(account => {
    const id = String(account.customer_id || "").trim();
    if (id) accountsById.set(id, account);
  });

  const query = searchQuery.trim().toLowerCase();

  return Array.from(
    new Map(
      monitoredAccountsData
        .map(account => {
          const id = String(
            account.account_id || account.customer_id || ""
          ).trim();
          if (!id || executiveIdSet.has(id)) return null;

          const monitorId = String(account.id || "").trim() || undefined;
          const sourceAccount = accountsById.get(id);
          const name =
            String(
              sourceAccount?.name ||
                account.account_name ||
                account.name ||
                "Unknown Account"
            ).trim() || "Unknown Account";

          const sponsorLabel =
            String(
              account.primary_sponsors || sourceAccount?.primary_sponsors || ""
            ).trim() || "Unassigned";

          return { id, name, monitorId, sponsorLabel };
        })
        .filter(Boolean)
        .map(account => [account!.id, account!])
    ).values()
  )
    .filter(account =>
      query ? account.name.toLowerCase().includes(query) : true
    )
    .sort((left, right) =>
      left.name.localeCompare(right.name, undefined, {
        sensitivity: "base",
      })
    );
}

export function getWatchListCustomerSponsorLabel(
  customer: WatchListCustomer,
  accountsCatalog: AccountCatalogItem[] = []
): string {
  const customerId = resolveWatchListCustomerId(customer, accountsCatalog);
  if (customerId) {
    const catalogAccount = accountsCatalog.find(
      account => String(account.customer_id || "").trim() === customerId
    );
    const fromCatalog = String(catalogAccount?.primary_sponsors || "").trim();
    if (fromCatalog) return fromCatalog;
  }

  const fromCustomer = String(customer["Primary Sponsors"] || "").trim();
  if (fromCustomer && fromCustomer !== "Unassigned") return fromCustomer;

  return "Unassigned";
}

export function mapWatchListCustomerToAccount(
  customer: WatchListCustomer
): Record<string, unknown> {
  const primary = String(customer["Primary Sponsors"] || "").trim();
  const secondary = String(customer["Secondary Sponsors"] || "").trim();

  return {
    customer_id: resolveWatchListCustomerId(customer) || customer.name || "",
    name: customer.name || "N/A",
    primary_sponsors: primary === "Unassigned" ? "" : primary,
    secondary_sponsors: secondary === "Unassigned" ? "" : secondary,
    assigned_at: customer.assigned_at ?? null,
    tier: customer.tier,
    region: customer.Location,
  };
}

/** Accounts for briefing panel from selected watch list(s). */
export function accountsForMonitoredBriefing(
  watchLists: WatchListItem[],
  selectedWatchlistId: string | null,
  accountsCatalog: ActiveCustomerWithSponsorsItem[]
): Array<Record<string, unknown>> {
  if (!selectedWatchlistId) return [];

  const lists = watchLists.filter(w => w.watch_list_id === selectedWatchlistId);

  const byId = new Map<string, ActiveCustomerWithSponsorsItem>();
  accountsCatalog.forEach(account => {
    const id = String(account.customer_id || "").trim();
    if (id) byId.set(id, account);
  });

  const byName = new Map<string, ActiveCustomerWithSponsorsItem>();
  accountsCatalog.forEach(account => {
    const name = String(account.name || "")
      .trim()
      .toLowerCase();
    if (name) byName.set(name, account);
  });

  const seen = new Set<string>();
  const result: Array<Record<string, unknown>> = [];

  lists.forEach(watchList => {
    (watchList.customers || []).forEach(customer => {
      const customerId = resolveWatchListCustomerId(customer, accountsCatalog);
      const nameKey = String(customer.name || "")
        .trim()
        .toLowerCase();
      const dedupeKey = customerId || nameKey;
      if (!dedupeKey || seen.has(dedupeKey)) return;
      seen.add(dedupeKey);

      const fromCatalog =
        (customerId && byId.get(customerId)) ||
        (nameKey && byName.get(nameKey));

      result.push(
        fromCatalog
          ? {
              ...fromCatalog,
              assigned_at: customer.assigned_at ?? null,
            }
          : mapWatchListCustomerToAccount(customer)
      );
    });
  });

  return result.sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""))
  );
}
